import dayjs, { Dayjs } from "dayjs";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CvpLocale } from "../constants/CvpLocale";
import { useEvaluatedLocale } from "./useEvaluatedLocale";

export interface DayjsLocaleContext {
  formatLoc: (date: string | Dayjs | Date, format: string) => string;
  fromNowLoc: (date: string | Dayjs | Date) => string;
  loadedLocale: CvpLocale;
}
const _DayjsLocaleContext = createContext<DayjsLocaleContext>(null);

export interface DayjsLocaleProviderProps {
  children: React.ReactNode;
}

export const DayjsLocaleProvider = ({ children }: DayjsLocaleProviderProps) => {
  const evaluatedLocale = useEvaluatedLocale();

  const [loadedLocale, setLoadedLocale] = useState<CvpLocale>(CvpLocale.en);

  const setDayjsLocale = async () => {
    await importDayjsLocale(evaluatedLocale);
    setLoadedLocale(evaluatedLocale);
  };

  useEffect(() => {
    setDayjsLocale();
  }, [evaluatedLocale]);

  // re-render all users of the context when the loadedLocale changes
  const contextValue = useMemo(() => {
    return {
      formatLoc: (date: string | Dayjs | Date, format: string) =>
        dayjs(date).locale(loadedLocale).format(format),
      // dayjs' global locale is never set, so a bare fromNow() is always English
      fromNowLoc: (date: string | Dayjs | Date) =>
        dayjs(date).locale(loadedLocale).fromNow(),
      loadedLocale,
    };
  }, [loadedLocale]);

  return (
    <_DayjsLocaleContext.Provider value={contextValue}>
      {children}
    </_DayjsLocaleContext.Provider>
  );
};

export const useDayjsLocalized = () => {
  return useContext(_DayjsLocaleContext);
};

async function importDayjsLocale(locale: CvpLocale) {
  switch (locale) {
    case CvpLocale.nb:
      return import("dayjs/locale/nb");
    case CvpLocale.en:
      return import("dayjs/locale/en");
    case CvpLocale.de:
      return import("dayjs/locale/de");
    case CvpLocale.fr:
      return import("dayjs/locale/fr");
    case CvpLocale.pl:
      return import("dayjs/locale/pl");
    case CvpLocale.da:
      return import("dayjs/locale/da");
    case CvpLocale.sv:
      return import("dayjs/locale/sv");
    default:
      throw new Error(`Unsupported locale: ${locale}`);
  }
}

/**
 * Dayjs does not have any localized date formats without year part. This function can remove the year part.
 * Tested with the locales we use and dates formatted with VISUAL_DATE_FORMAT = "l"
 * @param localizedFormatedDate
 * @returns
 */
export function removeYearFromFormatedDate(localizedFormatedDate: string) {
  // remove year part of the date
  localizedFormatedDate = localizedFormatedDate.replace(/\d{4}/, "");

  // trim starting and ending /.-
  localizedFormatedDate = localizedFormatedDate
    .replace(/^[\/\.-]/, "")
    .replace(/[\/\.-]$/, "");
  return localizedFormatedDate;
}
